import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEntitySchema, useTenantDetail } from "@/features/backoffice";
import { useClientId } from "@/features/utils/api-scope";
import { Link } from "react-router-dom";
import { EntityDetails } from "../../components/entity-details";
import { EntityHistory } from "../../components/entity-history";
import { SessionsExportCard } from "../components/sessions-export";

/**
 * The client as rows: what it holds, what its own record says, and every change
 * ever made to that record.
 *
 * The other tabs show the client through the screens it uses itself, which is
 * the right level for "is this handset registered". This one is the level below
 * — the one that answers "why does this tenant have 40 000 messages" or "who
 * changed its timezone last Tuesday" — and it is the door into the explorer:
 * every count links to that entity, already filtered on this client.
 */
export function ClientDataTab() {
  const clientId = useClientId();
  const { data, isLoading } = useTenantDetail(clientId);
  const { data: schema } = useEntitySchema("clients");

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Volumétrie</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : !data?.volumes.length ? (
            <p className="text-muted-foreground text-sm">
              Ce client n'a encore aucune donnée.
            </p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {data.volumes.map((volume) => (
                <Link
                  key={volume.table}
                  to={`/admin/explorer/${volume.table}?client_id=${clientId}`}
                  className="hover:border-primary/50 flex items-center justify-between rounded-sm border px-3 py-2 text-sm hover:bg-muted/40"
                >
                  <span className="truncate">{volume.label}</span>
                  <span className="tabular-nums font-medium">
                    {volume.count.toLocaleString("fr-FR")}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SessionsExportCard clientId={clientId} />

      <EntityDetails
        schema={schema}
        row={data?.client}
        isLoading={isLoading}
        title="Fiche client"
      />

      <EntityHistory table="clients" id={clientId} schema={schema} />
    </div>
  );
}
